/**
 * Pure option-building and label formatting for the outreach window, extracted
 * so the branching logic is unit-testable without rendering the form.
 *
 * The stored window is a pair of local wall-clock `HH:MM` strings. `start` is
 * the first minute the assistant may reach out; `end` is the minute it stops,
 * **exclusive** — so `12:00`–`13:00` is the 12:00–12:59 hour. The backend has
 * no `24:00` to write, so `23:59` is reserved for "through the end of the day"
 * and is the value behind the trailing "Midnight" end option.
 *
 * The dropdowns offer whole hours, but the stored window is not restricted to
 * them — the assistant's `user_update` tool accepts any `HH:MM`. A stored edge
 * that misses the hour grid is offered as its own option rather than dropped,
 * since a `selectedKey` the option list does not contain renders as the
 * placeholder and hides the member's real window from them.
 */

import { MIN_OUTREACH_WINDOW_MINUTES } from '@orn/global/outreach/outreachWindow.js';

import type { SelectItemType } from '@orn/shared-web/components/Select/Select';

/** The wire value for an end of day; the backend reads it as 24:00, not as 23:59. */
export const END_OF_DAY = '23:59';

/** `0`–`23` for a start, and additionally `24` for an end that runs to midnight. */
const MIDNIGHT_LABEL = 'Midnight';

/** `'13:00'` → `'1:00pm'`, `'09:35'` → `'9:35am'`, `'00:00'` and `'23:59'` → `'Midnight'`. */
export const formatClockLabel = (clock: string): string => {
  if (clock === '00:00' || clock === END_OF_DAY) {
    return MIDNIGHT_LABEL;
  }
  const hour = Number.parseInt(clock.slice(0, 2), 10);
  const minute = clock.slice(3, 5);
  const meridiem = hour < 12 ? 'am' : 'pm';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${minute}${meridiem}`;
};

/** Whether a stored edge lands on the hour grid the dropdowns offer. */
const isOnTheHour = (clock: string): boolean => clock === END_OF_DAY || clock.endsWith(':00');

/** `'09:00'` → `9`. An end of `'23:59'` reads as hour 24 — the exclusive end of the day. */
export const hourFromClock = (clock: string): number => {
  if (clock === END_OF_DAY) {
    return 24;
  }
  return Number.parseInt(clock.slice(0, 2), 10);
};

/** `9` → `'09:00'`, and `24` → `'23:59'`, the only end the backend accepts for midnight. */
export const clockFromHour = (hour: number): string =>
  hour === 24 ? END_OF_DAY : `${String(hour).padStart(2, '0')}:00`;

/** Minutes past midnight for a stored edge, reading `23:59` as the end of the day. */
const minutesFromClock = (clock: string): number =>
  clock === END_OF_DAY ? 24 * 60 : hourFromClock(clock) * 60 + Number.parseInt(clock.slice(3, 5), 10);

/** Whether `end` closes a window at least {@link MIN_OUTREACH_WINDOW_MINUTES} long after `start`. */
const isLongEnough = (start: string, end: string): boolean =>
  minutesFromClock(end) - minutesFromClock(start) >= MIN_OUTREACH_WINDOW_MINUTES;

/** The first whole hour an end may sit on, midnight at the latest. */
const firstEndHour = (start: string): number =>
  Math.min(Math.ceil((minutesFromClock(start) + MIN_OUTREACH_WINDOW_MINUTES) / 60), 24);

/** The latest start that still leaves a window of the minimum length before midnight. */
const LATEST_START_MINUTES = 24 * 60 - MIN_OUTREACH_WINDOW_MINUTES;

/** Minutes past midnight → `HH:MM`. */
const clockFromMinutes = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const clockOption = (clock: string): SelectItemType => ({ id: clock, label: formatClockLabel(clock) });

const hourOption = (hour: number): SelectItemType => clockOption(clockFromHour(hour));

/**
 * Insert an off-grid stored edge into an ascending option list, in its own
 * place. `23:59` sorts last by string comparison, which is also where midnight
 * belongs on an end list.
 */
const withStoredEdge = (options: SelectItemType[], stored: string): SelectItemType[] => {
  if (isOnTheHour(stored) || options.some((option) => option.id === stored)) {
    return options;
  }
  const at = options.findIndex((option) => option.id > stored);
  const index = at === -1 ? options.length : at;
  return [...options.slice(0, index), clockOption(stored), ...options.slice(index)];
};

/** Every hour a window may open on: midnight through 11pm. */
export const START_HOUR_OPTIONS: SelectItemType[] = Array.from({ length: 24 }, (_, hour) => hourOption(hour));

/** {@link START_HOUR_OPTIONS}, plus `start` itself when it misses the hour grid. */
export const startHourOptions = (start: string): SelectItemType[] => withStoredEdge(START_HOUR_OPTIONS, start);

/**
 * The hours a window may close on given where it opens — every hour at least an
 * hour after the start, with midnight last rather than first because it is the
 * *end* of the day here. A start of 11pm leaves midnight as the only close,
 * never an empty list. `end` is offered too when it misses the hour grid and is
 * still an hour past the start.
 */
export const endHourOptions = (start: string, end?: string): SelectItemType[] => {
  const firstHour = firstEndHour(start);
  const hours = Array.from({ length: 25 - firstHour }, (_, index) => hourOption(firstHour + index));
  return end !== undefined && isLongEnough(start, end) ? withStoredEdge(hours, end) : hours;
};

/**
 * The end to keep when the start moves. A start that leaves the current end
 * less than an hour away would leave the Select showing its placeholder, so the
 * window collapses to the first whole hour an hour past the new start.
 *
 * Compares the edges themselves rather than their hours, so an off-grid end
 * survives a start that still leaves it an hour away — truncating would move a
 * `22:40` end to `23:00` on a start of `21:30`, rewriting a value the member
 * never touched.
 */
export const clampEndToStart = (start: string, end: string): string =>
  isLongEnough(start, end) ? end : clockFromHour(firstEndHour(start));

/**
 * A stored window as the form can hold it. A row saved before the one-hour
 * floor can open after the latest start that leaves an hour, where no end is
 * late enough, or close within the hour; the start moves back to that latest
 * start and the end follows it. The form then only emits windows the backend
 * accepts, and writes the fitted one only when the member edits outreach.
 */
export const fitStoredWindow = ({ start, end }: { start: string; end: string }): { start: string; end: string } => {
  const fittedStart = minutesFromClock(start) > LATEST_START_MINUTES ? clockFromMinutes(LATEST_START_MINUTES) : start;
  return { start: fittedStart, end: clampEndToStart(fittedStart, end) };
};
